import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHead from "../../components/shared/SEOHead.jsx";
import JsonLd from "../../components/shared/JsonLd.jsx";
import { buildBreadcrumbJsonLd } from "../../utils/jsonld.js";
import SimplePage from "../../components/shared/SimplePage.jsx";
import { SUPPORT_PATH, SUPPORT_SUBPAGE_PATH } from "./paths.js";
import { SponsoringDe, SponsoringEn } from "./SponsoringContent.jsx";
import { MembershipDe, MembershipEn } from "./MembershipContent.jsx";
import { VolunteerDe, VolunteerEn } from "./VolunteerContent.jsx";

const KONTAKT_PATH = { de: "kontakt", en: "contact" };
const INFO_PATH = { de: "infos", en: "info" };
const SPONSORING_CONTENT = { de: SponsoringDe, en: SponsoringEn };
const MEMBERSHIP_CONTENT = { de: MembershipDe, en: MembershipEn };
const MEMBERSHIP_CTA = { de: "Jetzt Förderer werden", en: "Become a Supporter Now" };
const VOLUNTEER_CONTENT = { de: VolunteerDe, en: VolunteerEn };

/**
 * Generic value-proposition copy is fine to write here (no fabricated
 * org-specific facts) — but concrete numbers (membership fee, sponsoring
 * tiers) are left as placeholders, same treatment as Impressum's bank
 * details, since those are the association's decision, not code's.
 */
export default function SupportSubpage({ lang, topic }) {
  const { t } = useTranslation();
  const otherLang = lang === "de" ? "en" : "de";
  const path = `/${SUPPORT_PATH[lang]}/${SUPPORT_SUBPAGE_PATH[topic][lang]}`;
  const altPath = `/${SUPPORT_PATH[otherLang]}/${SUPPORT_SUBPAGE_PATH[topic][otherLang]}`;

  if (topic === "sponsoring") {
    const SponsoringBody = SPONSORING_CONTENT[lang];
    return (
      <>
        <SEOHead lang={lang} path={path} altPath={altPath} title={t(`support.${topic}.title`)} />
        <JsonLd
          data={buildBreadcrumbJsonLd(
            [
              { name: t("nav.home"), path: "/" },
              { name: t("nav.unterstuetzen"), path: `/${SUPPORT_PATH[lang]}` },
              { name: t(`support.${topic}.title`), path },
            ],
            lang
          )}
        />
        <SimplePage title={t(`support.${topic}.title`)}>
          <SponsoringBody />
          <Link to={`/${lang}/${INFO_PATH[lang]}`} className="tl-pill tl-pill-dark" style={{ alignSelf: "flex-start", padding: "12px 24px" }}>
            {t("support.contactCta")}
          </Link>
        </SimplePage>
      </>
    );
  }

  if (topic === "membership") {
    const MembershipBody = MEMBERSHIP_CONTENT[lang];
    return (
      <>
        <SEOHead lang={lang} path={path} altPath={altPath} title={t(`support.${topic}.title`)} />
        <JsonLd
          data={buildBreadcrumbJsonLd(
            [
              { name: t("nav.home"), path: "/" },
              { name: t("nav.unterstuetzen"), path: `/${SUPPORT_PATH[lang]}` },
              { name: t(`support.${topic}.title`), path },
            ],
            lang
          )}
        />
        <SimplePage title={t(`support.${topic}.title`)}>
          <MembershipBody />
          <Link to={`/${lang}/${KONTAKT_PATH[lang]}`} className="tl-pill tl-pill-dark" style={{ alignSelf: "flex-start", padding: "12px 24px" }}>
            {MEMBERSHIP_CTA[lang]}
          </Link>
        </SimplePage>
      </>
    );
  }

  if (topic === "volunteer") {
    const VolunteerBody = VOLUNTEER_CONTENT[lang];
    return (
      <>
        <SEOHead lang={lang} path={path} altPath={altPath} title={t(`support.${topic}.title`)} />
        <JsonLd
          data={buildBreadcrumbJsonLd(
            [
              { name: t("nav.home"), path: "/" },
              { name: t("nav.unterstuetzen"), path: `/${SUPPORT_PATH[lang]}` },
              { name: t(`support.${topic}.title`), path },
            ],
            lang
          )}
        />
        <SimplePage title={t(`support.${topic}.title`)}>
          <VolunteerBody />
          <Link to={`/${lang}/${KONTAKT_PATH[lang]}`} className="tl-pill tl-pill-dark" style={{ alignSelf: "flex-start", padding: "12px 24px" }}>
            {t("support.contactCta")}
          </Link>
        </SimplePage>
      </>
    );
  }

  return (
    <>
      <SEOHead lang={lang} path={path} altPath={altPath} title={t(`support.${topic}.title`)} />
      <SimplePage title={t(`support.${topic}.title`)}>
        <p style={{ fontSize: "16px", lineHeight: 1.7, color: "rgba(10,9,7,0.72)", margin: 0 }}>{t(`support.${topic}.body`)}</p>
        <p className="placeholder" style={{ fontSize: "14px", color: "rgba(10,9,7,0.7)", margin: 0 }}>
          {t(`support.${topic}.placeholder`)}
        </p>
        <Link to={`/${lang}/${KONTAKT_PATH[lang]}`} className="tl-pill tl-pill-dark" style={{ alignSelf: "flex-start", padding: "12px 24px" }}>
          {t("support.contactCta")}
        </Link>
      </SimplePage>
    </>
  );
}
